'use client'

import { FeatureFlag, useFeatureFlag } from '@/config/featureFlags'
import { paths } from '@/config/paths'
import Cards from '@/ui/assets/components/cards.inline.svg'
import { Button, LinkButton } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { ArrowRight, RefreshCw, Send, ShieldX, TrendingUp, Users } from 'lucide-react'
import { KYCStatus, WaitlistEntry } from '../types/CardState'
import { CardReferralShare } from './CardReferralShare'

interface CardNotPriorityAccessProps {
  waitlistEntry?: WaitlistEntry
  kycStatus?: KYCStatus
  onStartKYC: () => void
  onCheckStatus?: () => void
  isCheckingStatus?: boolean
}

function KYCStatusBadge({ status }: { status: KYCStatus }) {
  const config: Record<string, { label: string; className: string }> = {
    [KYCStatus.APPROVED]: { label: 'KYC Approved', className: 'border-green-500/30 bg-green-500/10 text-green-400' },
    [KYCStatus.PENDING]: { label: 'KYC Pending', className: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400' },
    [KYCStatus.REVIEW]: { label: 'KYC In Review', className: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400' },
    [KYCStatus.INITIATED]: {
      label: 'KYC Started',
      className: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400',
    },
    [KYCStatus.REJECTED]: { label: 'KYC Denied', className: 'border-red-500/30 bg-red-500/10 text-red-400' },
    [KYCStatus.RESUBMIT]: {
      label: 'Resubmission Required',
      className: 'border-orange-500/30 bg-orange-500/10 text-orange-400',
    },
    [KYCStatus.ONHOLD]: { label: 'KYC On Hold', className: 'border-orange-500/30 bg-orange-500/10 text-orange-400' },
  }

  const badge = config[status]
  if (!badge) return null

  return (
    <div className={cn('inline-flex items-center rounded-full border px-3 py-1 font-medium text-xs', badge.className)}>
      {badge.label}
    </div>
  )
}

export function CardNotPriorityAccess({
  waitlistEntry,
  kycStatus,
  onStartKYC,
  onCheckStatus,
  isCheckingStatus,
}: CardNotPriorityAccessProps) {
  const showReferrals = useFeatureFlag(FeatureFlag.SHOW_WAITLIST_REFERRALS)
  const isKYCDenied = kycStatus === KYCStatus.REJECTED
  const hasStartedKYC = kycStatus && kycStatus !== KYCStatus.NOT_REQUESTED && kycStatus !== KYCStatus.REQUESTED
  const isApprovedWaitlist = !!waitlistEntry && kycStatus === KYCStatus.APPROVED
  const title = waitlistEntry
    ? isApprovedWaitlist
      ? 'KYC Approved'
      : 'You\u2019re On The Waitlist'
    : 'Not Yet Eligible'
  const description = waitlistEntry
    ? isApprovedWaitlist
      ? 'Your identity is verified — you\u2019re now on the waitlist for the HypurrFi Card. Link your wallet to our Telegram bot below to receive an instant message the moment your access is activated.'
      : showReferrals
        ? 'Share your referral code to move up the line. Every person who completes KYC with your code boosts your position and gets you card access faster.'
        : hasStartedKYC
          ? 'You\u2019re on the waitlist for the HypurrFi Card. Link your wallet to our Telegram bot below to receive an instant message the moment your access is activated.'
          : 'You\u2019re on the waitlist for the HypurrFi Card. Complete identity verification below to unlock access — once verified, you\u2019ll be able to link your wallet to our Telegram bot for an instant message the moment your access is activated.'
    : 'You don\u2019t currently meet the eligibility criteria for the HypurrFi Card. Check back later as criteria may change.'

  if (isKYCDenied) {
    return (
      <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
        <div className="flex flex-1 flex-col gap-6">
          <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-red-500/10 via-transparent to-red-900/10" />

            <Panel.Content className="relative flex flex-col gap-6 p-8">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-red-500/30 bg-red-500/10">
                  <ShieldX className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <Typography variant="h3" className="text-red-400">
                    Verification Denied
                  </Typography>
                </div>
              </div>

              <Typography variant="p" className="text-muted-foreground">
                Unfortunately, your KYC verification was not approved. You are no longer eligible for the HypurrFi Card
                waitlist.
              </Typography>
            </Panel.Content>
          </Panel.Wrapper>
        </div>

        <div className="relative flex items-center justify-center lg:flex-1">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-[250px] w-[350px] rounded-full bg-gradient-to-br from-red-500/5 to-red-900/5 blur-[60px]" />
          </div>
          <div className="relative opacity-30 grayscale">
            <Cards className="h-auto w-full max-w-sm" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
      <div className="flex flex-1 flex-col gap-6">
        <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-primary/10 via-transparent to-gradient-green/10" />

          <Panel.Content className="relative flex flex-col gap-6 p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-primary/30 bg-primary/10">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <Typography variant="h3" gradient>
                  {title}
                </Typography>
              </div>
            </div>

            {kycStatus && hasStartedKYC && (
              <div className="flex items-center gap-2">
                <KYCStatusBadge status={kycStatus} />
              </div>
            )}

            <Typography variant="p" className="text-muted-foreground">
              {description}
            </Typography>

            {showReferrals && waitlistEntry && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-border/60 bg-background/30 p-4">
                  <Typography variant="prompt" className="text-muted-foreground">
                    Position
                  </Typography>
                  <Typography variant="h3">#{waitlistEntry.position}</Typography>
                </div>

                <div className="rounded-lg border border-border/60 bg-background/30 p-4">
                  <Typography variant="prompt" className="text-muted-foreground">
                    Referrals
                  </Typography>
                  <Typography variant="h3">{waitlistEntry.referralCount}</Typography>
                </div>

                <div className="rounded-lg border border-border/60 bg-background/30 p-4">
                  <Typography variant="prompt" className="text-muted-foreground">
                    Boost Score
                  </Typography>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <Typography variant="h3">{waitlistEntry.boostScore}</Typography>
                  </div>
                </div>
              </div>
            )}

            {!hasStartedKYC && (
              <Button
                onClick={onStartKYC}
                size="lg"
                className={cn(
                  'w-full',
                  'transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:shadow-primary/20',
                )}
                postfixIcon={<ArrowRight className="h-5 w-5" />}
              >
                Start KYC Verification
              </Button>
            )}

            {hasStartedKYC && onCheckStatus && (
              <div className="flex flex-col gap-3">
                <LinkButton
                  href={paths.cardTelegramNotifications}
                  variant="outline"
                  size="lg"
                  className="w-full gap-2 border-telegram/40 bg-telegram/5 text-telegram hover:border-telegram hover:bg-telegram/10 hover:text-telegram"
                  prefixIcon={<Send className="h-4 w-4" />}
                >
                  Get a Telegram alert when activated
                </LinkButton>
                <Button
                  onClick={onCheckStatus}
                  disabled={isCheckingStatus}
                  size="lg"
                  variant="outline"
                  className="w-full transition-all duration-300 hover:border-primary/50 hover:bg-primary/5"
                  prefixIcon={
                    isCheckingStatus ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )
                  }
                >
                  {isCheckingStatus ? 'Checking Status...' : 'Check Status'}
                </Button>
              </div>
            )}

            {showReferrals && (
              <div className="rounded-lg border border-border/50 bg-background/30 p-4">
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Each successful KYC completion from your referral code moves you closer to the front of the line.
                </Typography>
              </div>
            )}

            {showReferrals && waitlistEntry && <CardReferralShare referralCode={waitlistEntry.referralCode} />}
          </Panel.Content>
        </Panel.Wrapper>
      </div>

      <div className="relative flex items-center justify-center lg:flex-1">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-[250px] w-[350px] rounded-full bg-gradient-to-br from-primary/10 to-gradient-green/10 blur-[60px]" />
        </div>
        <div className="relative opacity-60 grayscale transition-all duration-500 hover:opacity-80 hover:grayscale-0">
          <Cards className="h-auto w-full max-w-sm" />
        </div>
      </div>
    </div>
  )
}
